import React from 'react';
import PropTypes from 'prop-types';
import { MemoryRouter, withRouter } from 'react-router-dom';
import BrowserHistoryContext from './BrowserHistoryContext';

export const LOCAL_STORAGE_KEY = 'osp:memory-router';

/**
 * Proxy component is intended to be a child for MemoryRouter
 * It receives the in-memory history instance and attach event listener on history route changes
 * https://reactrouter.com/core/api/MemoryRouter/children-node
 */
const ProxyComponent = withRouter(({ history, children, onHistoryChange }) => {
  React.useEffect(() => {
    const unlistenHistory = history.listen(onHistoryChange.bind(null, history));

    // detach history listener
    // https://github.com/ReactTraining/history/blob/master/docs/api-reference.md#history.listen
    return unlistenHistory;
  }, []);

  return children;
});

export const resetMemoryRouterStorage = () => window.localStorage.removeItem(LOCAL_STORAGE_KEY);

/**
 * Wrapper for React-Router MemoryRouter with the storing history location state into local storage
 * So, it will be recovered on page refresh
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const StatefulMemoryRouter = withRouter(({ history: browserHistory, module, children }) => {
  let routersState;

  try {
    routersState = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
  } catch (e) {
    routersState = {};
  }

  const { state: browserHistoryState } = browserHistory.location;
  let moduleRouterState = routersState[module] || { index: 0, entries: ['/'] };

  // reset the in-memory history of current module in case of  `{resetEntries: true}` state is provided
  if (browserHistoryState?.resetEntries) {
    browserHistory.replace({
      state: { ...browserHistoryState, resetEntries: undefined },
    });

    // do not re-render the current module in case of it's already on the root location
    if (moduleRouterState.index > 0) {
      routersState[module] = { index: 0, entries: ['/'] };
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(routersState));

      return null;
    }
  }

  // Navigate user to specific in-memory location in case of `{initialEntry: '/path/to/somewhere?some_param=xxx'}` state
  // is provided in browser history instance
  if (browserHistory.action === 'PUSH' && browserHistoryState?.initialEntry) {
    moduleRouterState = {
      index: 1,
      entries: ['/', browserHistoryState.initialEntry],
    };

    // reset `initialEntry` state to prevent this logic to be executed on page refresh
    browserHistory.replace({
      state: { ...browserHistoryState.state, initialEntry: undefined },
    });

    // Save the history state to the local storage
    routersState[module] = moduleRouterState;
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(routersState));
  }

  // Handle in-memory router state changes and save it into history state to the local storage
  const handleHistoryChange = (history) => {
    moduleRouterState.index = history.index;
    moduleRouterState.entries = history.entries;

    routersState[module] = moduleRouterState;

    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(routersState));
  };

  return (
    <BrowserHistoryContext.Provider value={browserHistory}>
      <MemoryRouter initialEntries={moduleRouterState.entries} initialIndex={moduleRouterState.index}>
        <ProxyComponent onHistoryChange={handleHistoryChange}>{children}</ProxyComponent>
      </MemoryRouter>
    </BrowserHistoryContext.Provider>
  );
});

StatefulMemoryRouter.propTypes = {
  module: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default StatefulMemoryRouter;
